import {Component, computed, DestroyRef, effect, inject, OnInit} from '@angular/core';
import {WidgetInfoItem} from 'src/app/dashboard/model/widget-info-item';
import {FinanceFilterModalService} from "../../service/finance-filter-modal.service";
import {MatDialog} from "@angular/material/dialog";
import {FinanceFilterModalComponent} from "../../components/finance-filter-modal/finance-filter-modal.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ActivatedRoute} from "@angular/router";
import {BehaviorSubject, firstValueFrom} from "rxjs";
import {TravelTypeModel} from "../../../general-ledger/model/general-ledger";
import {SubmitToResponse} from "../../../shared/model/pay";
import {TramsDataSourceModel} from "../../../payments/models/trams-data-source";
import {BranchResponse} from "../../../shared/model/bank";
import {DEFAULT_DATA_SOURCE_ID} from "../../constants/finance-filter-constants";
import {DashboardFinanceService} from '../../service/dashboard-finance.service';
import {SubmitToSalesActivityReportResponse} from '../../models/submit-to-sales-activity-report-response';
import {HttpParams} from '@angular/common/http';
import {FinanceFilterModel} from 'src/app/dashboard-finance/models/finance-filter-model';
import moment from 'moment';
import {FinanceDashboardChartPresentationType} from '../../enum/FinanceDashboardChartType';
import {TravelTypeSalesActivityReportResponse} from '../../models/travel-type-sales-activity-report-response';
import {ExternalTravelTypeSalesActivityReportResponse} from '../../models/external-travel-type-sales-activity-report-response';

@Component({
    selector: 'finance',
    templateUrl: './finance.component.html',
    standalone: false
})
export class FinanceComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly route = inject(ActivatedRoute);

  private readonly dialog = inject(MatDialog);

  private readonly financeFilterModalService = inject(FinanceFilterModalService);

  private readonly dashboardFinanceService = inject(DashboardFinanceService);

  private submitToSalesActivityReportResponse: SubmitToSalesActivityReportResponse[] = [];
  private salesActivityTravelTypeResponse: TravelTypeSalesActivityReportResponse[] = [];
  private salesActivityExternalTravelTypeResponse: ExternalTravelTypeSalesActivityReportResponse[] = [];

  commissionBySubmitToChartData: any;
  grossFareBySubmitToChartData: any;
  grossFareByTravelTypeChartData: any;
  commissionByTravelTypeChartData: any;
  yieldBySubmitToChartData: any;
  yieldByTravelTypeChartData: any;
  grossFareByExternalTravelTypeChartData: any;
  commissionByExternalTravelTypeChartData: any;

  public headerWidgetsData$ = new BehaviorSubject<WidgetInfoItem[]>([]);

  FinanceChartPresentationTypeEnum = FinanceDashboardChartPresentationType;
  grossFareSubmitToChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  commissionSubmitToChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeGrossTravelTypeChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeCommTravelTypeChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeYieldTravelTypeChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeYieldSubmitToChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeGrossExternalTravelTypeChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;
  financeCommExternalTravelTypeChartType: FinanceDashboardChartPresentationType = FinanceDashboardChartPresentationType.Amount;

  protected readonly selectedDataSource = computed(() => this.financeFilterModalService.selectedDataSource());

  constructor() {
    effect(async () => {
      const open = this.financeFilterModalService.toggleModal();
      if (!open) return;
      await this.openFilters();
    });
  }

  public ngOnInit() {
    this.getData();
  }

  private async getData() {
    const data = await firstValueFrom(this.route.data);
    const resolvedData: [TravelTypeModel[], SubmitToResponse, TramsDataSourceModel[], BranchResponse] = data['data'];

    const TRAVEL_TYPES = resolvedData[0];
    const DEFAULT_TRAVEL_TYPES = TRAVEL_TYPES.map(t => t.id);

    const SUBMIT_TO = resolvedData[1].items;
    const DEFAULT_SUBMIT_TO = SUBMIT_TO.map(s => s.submitToLinkCode);

    const BRANCHES = resolvedData[3].items;
    const FILTERED_BRANCHES = BRANCHES.filter(b => b.dataSourceId === DEFAULT_DATA_SOURCE_ID);
    const DEFAULT_BRANCHES = FILTERED_BRANCHES
      .map(b => b.id);

    const { dateFrom, dateTo } = this.financeFilterModalService.dates;
    this.financeFilterModalService.setFilters({
      TravelTypeIds: DEFAULT_TRAVEL_TYPES,
      SubmitToList: DEFAULT_SUBMIT_TO,
      IssueDateFrom: dateFrom,
      IssueDateTo: dateTo,
      DataSourceId: DEFAULT_DATA_SOURCE_ID,
      BranchIds: DEFAULT_BRANCHES,
    });

    this.financeFilterModalService.dataSources.set(data['data'][2]);
    this.getSalesActivityReports();
  }

  private async openFilters() {
    const data = await firstValueFrom(this.route.data);
    const dialog = this.dialog.open(FinanceFilterModalComponent,
    {
      data,
    });
    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.financeFilterModalService.toggleModal.set(false);
      });
  }

  private getSalesActivityReports() {
    this.financeFilterModalService.getFilters()
      .subscribe((filters: FinanceFilterModel) => {
        let httpParams = new HttpParams({
          fromObject: {
            TravelTypeIds: filters.TravelTypeIds,
            SubmitToList: filters.SubmitToList,
            IssueDateFrom: filters.IssueDateFrom ? moment(filters.IssueDateFrom.toISOString()).format('YYYY-MM-DD') : '',
            IssueDateTo: filters.IssueDateTo ? moment(filters.IssueDateTo.toISOString()).format('YYYY-MM-DD') : '',
            DataSourceId: filters.DataSourceId && filters.DataSourceId > 0 ? filters.DataSourceId : '',
            BranchIds: filters.BranchIds
          }
        });

        this.dashboardFinanceService.salesActivityReportByTravelType(httpParams)
          .subscribe((data: any) => {
            this.salesActivityTravelTypeResponse = data.items as TravelTypeSalesActivityReportResponse[];

            this.buildGrossFareByTravelTypeChartData();
            this.buildCommissionByTravelTypeChartData();
            this.buildYieldByTravelTypeChartData();

            const grossFareTravelType = this.getTotal(data.totals, "TravelTypeTotalFareSum");
            const grossFarePercentTravelType = this.getTotal(data.totals, "TravelTypePercentToTotalFare");
            const netFareTravelType = this.getTotal(data.totals, "TravelTypeNetFareSum");
            const netFarePercentTravelType = this.getTotal(data.totals, "TravelTypePercentYield");
            this.headerWidgetsData$.next([
              {
                text: `$${grossFareTravelType.toFixed(2)}`,
                title: 'Travel Type Total Gross Fare',
                overLine: `Increased ${grossFarePercentTravelType.toFixed(2)}%`,
                icon: 'attach_money',
                color: 'success'
              },
              {
                text: `$${netFareTravelType.toFixed(2)}`,
                title: 'Travel Type Total Yield',
                overLine: `Increased ${netFarePercentTravelType.toFixed(2)}%`,
                icon: 'show_chart',
                color: 'info'
              }
            ]);

            this.dashboardFinanceService.salesActivityReportByExternalTravelType(httpParams)
              .subscribe((data: any) => {
                this.salesActivityExternalTravelTypeResponse = data.items as ExternalTravelTypeSalesActivityReportResponse[];

                this.buildGrossFareByExternalTravelTypeChartData();
                this.buildCommissionByExternalTravelTypeChartData();
              });

            this.dashboardFinanceService.salesActivityReportBySubmitTo(httpParams)
              .subscribe((data: any) => {
                this.submitToSalesActivityReportResponse = data.items as SubmitToSalesActivityReportResponse[];

                this.buildCommissionBySubmitToChartData();
                this.buildGrossFareBySubmitToChartData();
                this.buildYieldBySubmitToChartData();

                const grossFareSubmitTo = this.getTotal(data.totals, "SubmitToTotalFareSum");
                const grossFarePercentSubmitTo = this.getTotal(data.totals, "SubmitToPercentToTotalFare");
                const netFareSubmitTo = this.getTotal(data.totals, "SubmitToNetFareSum");
                const netFarePercentSubmitTo = this.getTotal(data.totals, "SubmitToPercentYield");
                const current = this.headerWidgetsData$.value ?? [];
                this.headerWidgetsData$.next([
                  ...current,
                  {
                    text: `$${grossFareSubmitTo.toFixed(2)}`,
                    title: 'Submit To Total Gross Fare',
                    overLine: `Increased ${grossFarePercentSubmitTo.toFixed(2)}%`,
                    icon: 'attach_money',
                    color: 'success'
                  },
                  {
                    text: `$${netFareSubmitTo.toFixed(2)}`,
                    title: 'Submit To Total Yield',
                    overLine: `Increased ${netFarePercentSubmitTo.toFixed(2)}%`,
                    icon: 'show_chart',
                    color: 'info'
                  }
                ]);
              });
            });
      });
  }

  private getTotal(totals: { columnName: string, total: number }[], columnName: string): number {
    return totals.find(t => t.columnName === columnName)?.total ?? 0;
  }

  private buildCommissionBySubmitToChartData() {
    this.commissionBySubmitToChartData =
    {
      labels: this.submitToSalesActivityReportResponse.map((d) => `${d.submitToLink}`),
      amount: this.submitToSalesActivityReportResponse.map((d) => d.submitToCommAmountSum),
      percentage: this.submitToSalesActivityReportResponse.map((d) => d.submitToPercentComm)
    };
  }

  private buildGrossFareBySubmitToChartData() {
    this.grossFareBySubmitToChartData =
    {
      labels: this.submitToSalesActivityReportResponse.map((d) => `${d.submitToLink}`),
      amount: this.submitToSalesActivityReportResponse.map((d) => d.submitToTotalFareSum),
      percentage: this.submitToSalesActivityReportResponse.map((d) => d.submitToPercentToTotalFare),
    };
  }

  private buildYieldBySubmitToChartData() {
    this.yieldBySubmitToChartData =
    {
      labels: this.submitToSalesActivityReportResponse.map((d) => `${d.submitToLink}`),
      amount: this.submitToSalesActivityReportResponse.map((d) => d.submitToNetFareSum),
      percentage: this.submitToSalesActivityReportResponse.map((d) => d.submitToPercentYield)
    };
  }

  private buildGrossFareByTravelTypeChartData() {
    const data = this.salesActivityTravelTypeResponse.filter(d => d.travelTypeTotalFareSum > 0);
    this.grossFareByTravelTypeChartData =
    {
      labels: data.map((d) => `${d.travelTypeName}`),
      amount: data.map((d) => d.travelTypeTotalFareSum),
      percentage: data.map((d) => d.travelTypePercentToTotalFare)
    };
  }

  private buildCommissionByTravelTypeChartData() {
    const data = this.salesActivityTravelTypeResponse.filter(d => d.travelTypeCommAmountSum > 0);
    this.commissionByTravelTypeChartData =
    {
      labels: data.map((d) => `${d.travelTypeName}`),
      amount: data.map((d) => d.travelTypeCommAmountSum),
      percentage: data.map((d) => d.travelTypePercentComm)
    };
  }

  private buildYieldByTravelTypeChartData() {
    this.yieldByTravelTypeChartData =
    {
      labels: this.salesActivityTravelTypeResponse.map((d) => `${d.travelTypeName}`),
      amount: this.salesActivityTravelTypeResponse.map((d) => d.travelTypeNetFareSum),
      percentage: this.salesActivityTravelTypeResponse.map((d) => d.travelTypePercentYield)
    };
  }

  private buildGrossFareByExternalTravelTypeChartData() {
    const data = this.salesActivityExternalTravelTypeResponse.filter(d => d.externalTravelServiceTypeTotalFareSum > 0);
    this.grossFareByExternalTravelTypeChartData =
    {
      labels: data.map((d) => `${d.externalTravelServiceTypeName}`),
      amount: data.map((d) => d.externalTravelServiceTypeTotalFareSum),
      percentage: data.map((d) => d.externalTravelServiceTypePercentToTotalFare)
    };
  }

  private buildCommissionByExternalTravelTypeChartData() {
    const data = this.salesActivityExternalTravelTypeResponse.filter(d => d.externalTravelServiceTypeCommAmountSum > 0);
    this.commissionByExternalTravelTypeChartData =
    {
      labels: data.map((d) => `${d.externalTravelServiceTypeName}`),
      amount: data.map((d) => d.externalTravelServiceTypeCommAmountSum),
      percentage: data.map((d) => d.externalTravelServiceTypePercentComm)
    };
  }
}
